import React from 'react';
import { MapPin, Phone, Clock, Star, Navigation, Stethoscope, Wifi, Car, Building2, Users, Shield } from 'lucide-react';

const HospitalCard = ({ 
  hospital, 
  viewMode = 'grid', 
  showDistance = false, 
  onViewDetails, 
  onCall,
  className = '' 
}) => {
  
  
  const getProfileImage = (hospital) => {
    
    if (hospital.profileImage) {
      return hospital.profileImage;
    }
    
    if (hospital.images && hospital.images.length > 0) {
      // Try to find primary image first
      const primaryImage = hospital.images.find(img => img.isPrimary);
      if (primaryImage?.url) {
        return primaryImage.url;
      }
      // Fall back to first image
      return hospital.images[0].url;
    }
    
    if (hospital.user?.profileImage) {
      return hospital.user.profileImage;
    }
    
    return null;
  };

  const formatDistance = (distance) => {
    if (!distance) return null;
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m away`;
    }
    return `${distance}km away`;
  };

  const getOperatingStatus = (operatingHours) => {
    if (!operatingHours) return { status: 'Hours not available', color: 'text-gray-500' };
    
    if (operatingHours.is24Hours) {
      return { status: '24/7 Open', color: 'text-green-600', icon: '🟢' };
    }
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinutes;
    const currentDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];
    
    const todayHours = operatingHours[currentDay];
    if (!todayHours || !todayHours.open || !todayHours.close) {
      return { status: 'Closed Today', color: 'text-red-500', icon: '🔴' };
    }
    
    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
    const openTime = openHour * 60 + (openMin || 0);
    const closeTime = closeHour * 60 + (closeMin || 0);
    
    if (currentTime >= openTime && currentTime < closeTime) {
      return { status: `Open until ${todayHours.close}`, color: 'text-green-600', icon: '🟢' };
    } else {
      return { status: `Opens at ${todayHours.open}`, color: 'text-orange-500', icon: '🟡' };
    }
  };

  const renderRating = (rating) => {
    if (!rating?.average) {
      return (
        <div className="flex items-center gap-1 text-gray-400">
          <Star size={16} />
          <span className="text-sm">No ratings</span>
        </div>
      );
    }
    
    const stars = [];
    const fullStars = Math.floor(rating.average);
    const hasHalfStar = rating.average % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative">
            <Star size={14} className="text-gray-300" />
            <Star 
              size={14} 
              className="absolute top-0 left-0 fill-yellow-400 text-yellow-400" 
              style={{ clipPath: 'inset(0 50% 0 0)' }} 
            />
          </div>
        );
      } else {
        stars.push(<Star key={i} size={14} className="text-gray-300" />);
      }
    }
    
    return (
      <div className="flex items-center gap-1">
        <div className="flex gap-0.5">{stars}</div>
        <span className="text-sm text-gray-600 ml-1">
          {rating.average.toFixed(1)} ({rating.count || 0})
        </span>
      </div>
    );
  };

  const getMainPhoneNumber = (contact) => {
    if (!contact) return null;
    return contact.phone || contact.emergency || contact.appointment;
  };

  const handleCall = (e) => {
    e.stopPropagation();
    const phone = getMainPhoneNumber(hospital.contact);
    if (phone && onCall) {
      onCall(phone);
    }
  };

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(hospital);
    }
  };

  const operatingStatus = getOperatingStatus(hospital.operatingHours);
  const mainPhone = getMainPhoneNumber(hospital.contact);
  const distance = hospital.distance || hospital.calculatedDistance;
  const profileImageUrl = getProfileImage(hospital);

  if (viewMode === 'list') {
    return (
      <div className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer border border-gray-200 hover:border-primary-300 ${className}`}>
        <div className="flex h-40">
          {/* Hospital Image */}
          <div className="w-48 flex-shrink-0 relative bg-gray-100">
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt={hospital.name}
                className="w-full h-full object-cover rounded-l-lg"
                crossOrigin="anonymous"
                onError={(e) => {
                  console.error('Failed to load image:', profileImageUrl);
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`w-full h-full flex items-center justify-center rounded-l-lg ${profileImageUrl ? 'hidden' : 'flex'}`}>
              <Building2 className="w-16 h-16 text-gray-400" />
            </div>
            
            {hospital.isVerified && (
              <div className="absolute top-2 left-2 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                <Shield size={12} />
                Verified
              </div>
            )}
            {distance && showDistance && (
              <div className="absolute top-2 right-2 bg-primary-600 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                <Navigation size={12} />
                {formatDistance(distance)}
              </div>
            )}
          </div>

          {/* Hospital Info */}
          <div className="flex-1 p-4 flex flex-col justify-between">
            <div>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-lg text-gray-900 line-clamp-1">
                  {hospital.name}
                </h3>
                {renderRating(hospital.rating)}
              </div>

              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <MapPin size={14} />
                <span className="text-sm line-clamp-1">{hospital.location?.address}</span>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <Clock size={14} className={operatingStatus.color} />
                <span className={`text-sm ${operatingStatus.color} font-medium`}>
                  {operatingStatus.status}
                </span>
              </div>

              {hospital.services?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {hospital.services.slice(0, 3).map((service, index) => (
                    <span
                      key={index}
                      className="bg-primary-50 text-primary-700 px-2 py-1 rounded text-xs"
                    >
                      {service.name}
                    </span>
                  ))}
                  {hospital.services.length > 3 && (
                    <span className="text-gray-500 text-xs px-2 py-1">
                      +{hospital.services.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <div className="flex gap-2">
                {distance && showDistance && (
                  <div className="flex items-center gap-1 text-primary-600 text-sm font-medium">
                    <Navigation size={14} />
                    {formatDistance(distance)}
                  </div>
                )}
                {hospital.capacity?.beds && (
                  <div className="flex items-center gap-1 text-gray-500 text-sm">
                    <Users size={14} />
                    {hospital.capacity.beds} beds
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                {mainPhone && (
                  <button
                    onClick={handleCall}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    <Phone size={14} />
                    Call
                  </button>
                )}
                <button
                  onClick={handleViewDetails}
                  className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium"
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div 
      className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 hover:border-primary-300 overflow-hidden ${className}`}
      onClick={handleViewDetails}
    >
      {/* Hospital Image */}
      <div className="relative h-48 bg-gray-100">
        {profileImageUrl ? (
          <img
            src={profileImageUrl}
            alt={hospital.name}
            className="w-full h-full object-cover"
            crossOrigin="anonymous"
            onError={(e) => {
              console.error('Failed to load image:', profileImageUrl);
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`absolute inset-0 flex items-center justify-center ${profileImageUrl ? 'hidden' : 'flex'}`}>
          <Building2 className="w-20 h-20 text-gray-400" />
        </div>
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {hospital.isVerified && (
            <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <Shield size={12} />
              Verified
            </div>
          )}
          
          {distance && showDistance && (
            <div className="bg-primary-600 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <Navigation size={12} />
              {formatDistance(distance)}
            </div>
          )}
        </div>

        {/* Operating Status */}
        <div className="absolute top-3 right-3">
          <div className={`bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium ${operatingStatus.color} flex items-center gap-1`}>
            <Clock size={12} />
            {operatingStatus.status}
          </div>
        </div>
      </div>

      {/* Hospital Info */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-lg text-gray-900 line-clamp-2 flex-1 mr-2">
            {hospital.name}
          </h3>
          {renderRating(hospital.rating)}
        </div>

        <div className="flex items-center gap-2 text-gray-600 mb-3">
          <MapPin size={14} className="flex-shrink-0" />
          <span className="text-sm line-clamp-2">{hospital.location?.address}</span>
        </div>

        {/* Services */}
        {hospital.services?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {hospital.services.slice(0, 2).map((service, index) => (
              <span
                key={index}
                className="bg-primary-50 text-primary-700 px-2 py-1 rounded-md text-xs font-medium"
              >
                {service.name}
              </span>
            ))}
            {hospital.services.length > 2 && (
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs">
                +{hospital.services.length - 2} more
              </span>
            )}
          </div>
        )}

        {/* Additional Info */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
          <div className="flex items-center gap-3">
            {hospital.capacity?.beds && (
              <div className="flex items-center gap-1">
                <Users size={12} />
                {hospital.capacity.beds} beds
              </div>
            )}
            {hospital.facilities?.some(f => f.name === 'WiFi') && (
              <Wifi size={12} className="text-primary-500" />
            )}
            {hospital.facilities?.some(f => f.name === 'Parking') && (
              <Car size={12} className="text-primary-500" />
            )}
            {hospital.facilities?.some(f => f.name === 'Wheelchair Access') && (
              <div className="w-3 h-3 bg-primary-500 rounded-full" title="Wheelchair Accessible" />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-3 border-t border-gray-100">
          {mainPhone && (
            <button
              onClick={handleCall}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <Phone size={14} />
              Call Now
            </button>
          )}
          <button
            onClick={handleViewDetails}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <Stethoscope size={14} />
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default HospitalCard;